import { randomUUID } from 'crypto';
import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getDatabase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const triggersCommand = new Command('triggers')
  .description('Manage triggers (outbound webhooks, internal writes, external writes)');

triggersCommand.command('create')
  .description('Create a trigger (a webhook, an internal_write that patches a referenced document, or an external_write that mirrors the document to an external source)')
  .requiredOption('--name <name>', 'Trigger name')
  .requiredOption('--events <events>', 'Comma-separated event types')
  .option('--url <url>', 'Webhook target URL (webhook actions)')
  .option('--secret <secret>', 'Webhook HMAC signing secret (webhook actions)')
  .option('--action <json>', 'Action JSON. webhook {"type":"webhook","url":...,"secret":...}; internal_write {"type":"internal_write","target":"slots","match":{"source_field":"data.slot_id"},"patch":{"status":"busy"},"precondition":{"field":"data.status","op":"eq","value":"free"},"mode":"async"} (mode: "async" applies after the write, "transactional" applies atomically so a precondition miss rolls the write back); external_write {"type":"external_write","collection":"upstream_appointments","source":"legacy","op":"create","watched_fields":"mapped"} (writes the triggering document through that source\'s write path — translation, endpoint, auth — and links the upstream-assigned id back as external_id; "collection" defaults to the triggering collection, "op" defaults from the event; "watched_fields" scopes UPDATE dispatch to writes that changed an upstream-mapped field — "mapped" = the source\'s to_external keys, or an explicit array of data fields — so an overlay-only write skips the redundant upstream push; omit to fire on every update)')
  .option('--id <id>', 'Trigger ID (auto-generated if omitted)')
  .option('--collection-scope <collections>', 'Comma-separated collection scope')
  .option('--filter <json>', 'Filter expression (JSON)')
  .option('--skip-cascade-writes <bool>', 'Skip firing on cascade-originated (internal_write) writes (default true)')
  .action(async function (this: Command, opts: { name: string; url?: string; secret?: string; action?: string; events: string; id?: string; collectionScope?: string; filter?: string; skipCascadeWrites?: string }) {
    if (!opts.action && !opts.url) {
      throw new Error('Provide --url (webhook) or --action <json> (e.g. an internal_write action).');
    }
    const ws = getDatabase(this);
    const client = new ApiClient();
    const triggerId = opts.id ?? randomUUID();
    const body: Record<string, unknown> = {
      name: opts.name,
      events: opts.events.split(','),
      enabled: true,
    };
    if (opts.action) {
      body['action'] = JSON.parse(opts.action);
    } else {
      // Legacy flat shape — normalized into a webhook action server-side.
      body['url'] = opts.url;
      if (opts.secret) body['secret'] = opts.secret;
    }
    if (opts.collectionScope) {
      body['collection_scope'] = opts.collectionScope.split(',');
    }
    if (opts.filter) {
      body['filter'] = JSON.parse(opts.filter);
    }
    if (opts.skipCascadeWrites !== undefined) {
      body['skip_cascade_writes'] = opts.skipCascadeWrites !== 'false';
    }
    const data = await client.request('PUT', `/v1/${ws}/triggers/${triggerId}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

triggersCommand.command('get <id>')
  .description('Get a trigger')
  .action(async function (this: Command, id: string) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/triggers/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

triggersCommand.command('list')
  .description('List all triggers')
  .action(async function (this: Command) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/triggers`);
    console.log(formatOutput(data, getFormat(this)));
  });

triggersCommand.command('deliveries')
  .description('List trigger delivery attempts (status, retries, errors)')
  .option('--status <status>', 'Filter by status (pending | delivered | failed | dead)')
  .option('--trigger-id <id>', 'Filter by trigger id')
  .action(async function (this: Command, opts: { status?: string; triggerId?: string }) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.triggerId) params.set('trigger_id', opts.triggerId);
    const qs = params.toString();
    const data = await client.request('GET', `/v1/${ws}/triggers/deliveries${qs ? `?${qs}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });

triggersCommand.command('delete <id>')
  .description('Delete a trigger')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete trigger ${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getDatabase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/triggers/${id}`);
    console.log('Deleted');
  });
